//#region Libraries
import { useState } from "react";
//#endregion
//#region Components
import CustomButton from "components/CustomButton/CustomButton";
import DialogAlertAction from "components/DialogAlertAction/DialogAlertAction";
import FooterButtons from "../../components/FooterButtons/FooterButtons";
//#endregion
//#region Services
import { requestCreateElectronicService, requestUpdateElectronicService } from "services/resources/library/aboutLibraryResources";
//#endregion
//#region Utils
import useMainContext from "utils/context/useMainContext";
import { RESOURCE_TEXT_LIST } from "views/Recursos/ResourcesLibrary/utils/constants";
//#endregion

const Footer = ({
    isAdmin,
    closeFormDialog,
    toggleDialogShowDeletionResource,
    getBrandName,
    form, loadingRequest,
    filtersAreCompleted,
    fillServiceList,
    currentElectronicResourceForUpdate,
    isCreate
}) => {
    const { isResource } = form;
    const mainContext = useMainContext();
    //#region States
    const [showDialogSaveChanges, setShowDialogSaveChanges] = useState(false);
    const [contentPropsDialogSaveChanges, setContentPropsDialogSaveChanges] = useState({
        title: "",
        contentComponent: null
    });
    //#endregion
    //#region Functions
    const saveData = async () => {
        const {
            id, resourceTypeId, campusId, 
            libraryId, name, brandId,
            barcode, details
        } = form.value;
        const serviceTypeName = RESOURCE_TEXT_LIST[resourceTypeId].minus.singular;
        // Realizando petición de creación o actualización
        let requestBody = {
            id, resourceTypeId, campusId,
            libraryId, name, brandId,
            barcode, details
        };
        if (isResource.tablet) {
            const { 
                model,
                serial
            } = form.value;
            requestBody = {
                ...requestBody,
                model,
                serial
            };
        }
        const params = isCreate 
            ? {
                request: requestCreateElectronicService,
                loadingRequestMessage: `Registrando ${serviceTypeName}...`,
                toastMessage: `¡Se registró exitosamente el recurso ${name}!`
            }
            : {
                request: requestUpdateElectronicService,
                loadingRequestMessage: `Actualizando ${serviceTypeName}...`,
                toastMessage: `¡Actualización con éxito de ${name}!`
            };
        loadingRequest.show(params.loadingRequestMessage);
        const success = await params.request(requestBody);
        loadingRequest.hide();
        // Gestionando respuesta
        if (success) {
            // Exitosa
            closeFormDialog();
            fillServiceList({
                messageForToast: params.toastMessage,
                noFill: !filtersAreCompleted()
            });
            return;
        }
        // Fallida
        mainContext.setShowDialogUnexpectedError(true);
    }
    const toggleShowDialogSaveChanges = (value = null) => {
        setShowDialogSaveChanges(value !== null ? value : !showDialogSaveChanges);
    }
    const checkIfThereAreMoreThanTwoChanges = quantityChanges => {
        quantityChanges++;
        if (quantityChanges > 1) 
            throw new Error();
        return quantityChanges;
    }
    const getFieldToUpdate = () => {
        // Obteniendo información sobre el / los campo(s) a modificar
        let quantityChanges = 0;
        let fieldToUpdate = null;
        const { 
            campusId,
            libraryId,
            brandId,
            barcode,
            details
        } = form.value;
        if (campusId !== currentElectronicResourceForUpdate.campusId) {
            fieldToUpdate = {
                field: "Campus",
                article: "el",
                newValue: campusId
            };
            quantityChanges = checkIfThereAreMoreThanTwoChanges(quantityChanges);
        }
        if (libraryId !== currentElectronicResourceForUpdate.libraryId) {
            fieldToUpdate = {
                field: "Biblioteca",
                article: "la",
                newValue: libraryId
            };
            quantityChanges = checkIfThereAreMoreThanTwoChanges(quantityChanges);
        }
        if (brandId !== currentElectronicResourceForUpdate.brandId) {
            fieldToUpdate = {
                field: "Marca",
                article: "la",
                newValue: getBrandName(brandId)
            };
            quantityChanges = checkIfThereAreMoreThanTwoChanges(quantityChanges);
        }
        if (barcode !== currentElectronicResourceForUpdate.barcode) {
            fieldToUpdate = {
                field: "Código de barras",
                article: "el",
                newValue: barcode
            };
            quantityChanges = checkIfThereAreMoreThanTwoChanges(quantityChanges);
        }
        if (details !== currentElectronicResourceForUpdate.details) {
            fieldToUpdate = {
                field: "Detalles",
                article: "los",
                newValue: details
            };
            quantityChanges = checkIfThereAreMoreThanTwoChanges(quantityChanges);
        }
        if (isResource.tablet) {
            const { model, serial } = form.value;
            if (model !== currentElectronicResourceForUpdate.model) {
                fieldToUpdate = {
                    field: "Modelo",
                    article: "el",
                    newValue: model
                };
                quantityChanges = checkIfThereAreMoreThanTwoChanges(quantityChanges);
            }
            if (serial !== currentElectronicResourceForUpdate.serial) {
                fieldToUpdate = {
                    field: "Serie",
                    article: "la",
                    newValue: serial
                };
                checkIfThereAreMoreThanTwoChanges(quantityChanges);
            }
        }
        return fieldToUpdate;
    }
    const openDialogSaveChanges = () => {
        const isCompleted = form.isCompleted();
        if (!isCompleted) return;
        // Abriendo modal de confirmación de guardado de cambios
        toggleShowDialogSaveChanges(true);
        const { name } = form.value;
        if (isCreate) {
            setContentPropsDialogSaveChanges({
                title: `¿Deseas registrar el recurso ${name}?`,
                contentComponent: (
                    <p className="paragraph">
                        Recuerda que podrás visualizarlo en la pantalla principal.
                    </p>
                )
            });
            return;
        }
        const newContentPropsDialogSaveChanges = {
            title: `¿Deseas guardar el cambio en el recurso ${name}?`,
            contentComponent: null
        };
        try {
            const fieldToUpdate = getFieldToUpdate();
            newContentPropsDialogSaveChanges.contentComponent = (
                <p className="paragraph">
                    Recuerda que ahora el recurso <b></b> tendrá {fieldToUpdate.article} {fieldToUpdate.field} <b>{fieldToUpdate.newValue}</b>
                </p>
            );
        }
        catch (err) {
            newContentPropsDialogSaveChanges.contentComponent = (
                <p className="paragraph">
                    Recuerda que podrás visualizar los cambios en la opción <b>Editar.</b>
                </p>
            );
        }
        finally {
            setContentPropsDialogSaveChanges(newContentPropsDialogSaveChanges);
        }
    }
    const getDisabledSaveButton = () => {
        return !form.areThereChanges() || !form.isCompleted();
    }
    //#endregion
    const canDelete = !isCreate && isAdmin;
    return (
        <>
        <FooterButtons 
            canDelete={canDelete}
            actions={{
                doSave: openDialogSaveChanges,
                doCancel: closeFormDialog,
                doDelete: () => toggleDialogShowDeletionResource(true)
            }}
            form={form}
            getDisabledSaveButton={getDisabledSaveButton}/>
        <DialogAlertAction
            minWidth="590px"
            widthContent={"90%"}
            spacingContainer="16px"
            variant="secondary"
            show={showDialogSaveChanges}
            toggleShow={toggleShowDialogSaveChanges}
            {...contentPropsDialogSaveChanges}>
            <CustomButton
                variant="secondary"
                size="medium"
                text="No, cancelar"
                width="110px"
                onClick={() => toggleShowDialogSaveChanges(false)}/>
            <CustomButton
                size="medium"
                text="Sí, guardar"
                width="110px"
                onClick={saveData}/>
        </DialogAlertAction>
        </>
    );
}

export default Footer;